import { useRef, useState, LegacyRef, useEffect } from 'react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { Box, Button, MenuButton, useOutsideClick } from '@chakra-ui/react'
import { DARK_GREY, DARK_TABLE_HEADER_BG, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import { Token, MinWidth, Width } from 'types/chakra'
import { IDropdown } from 'types/common'

interface IDropdownButtonProps {
  item?: IDropdown
  placeHolder?: string
  isOpen: boolean
  minWidth?: Token<MinWidth | number, 'sizes'>
  width?: Token<Width | number, 'sizes'>
  height?: Token<Width | number, 'sizes'>
  isTable?: boolean
  border?: string
  /** Override the trigger surface so a dropdown can match the inputs beside it. */
  background?: string
  fontSize?: string
  isDisabled?: boolean
  titleButton?: string
  /** Opt-in dark-surface styling (default light). */
  dark?: boolean
}

const DropdownButton = (props: IDropdownButtonProps) => {
  const {
    item,
    placeHolder,
    isOpen,
    width,
    isTable,
    height,
    border,
    background,
    fontSize,
    isDisabled,
    titleButton,
    dark
  } = props
  const itemTitle = titleButton ? `${titleButton}${item?.title}` : (item?.title ?? '')
  const menuButtonRef: LegacyRef<HTMLButtonElement> = useRef(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)

  useOutsideClick({
    ref: menuButtonRef,
    handler: () => setIsFocused(false)
  })

  useEffect(() => {
    if (isOpen) {
      setIsFocused(true)
    }
  }, [isOpen])

  return (
    <MenuButton
      ref={menuButtonRef}
      width={width ?? { base: '400px', lg: '178px' }}
      isActive={isOpen}
      noOfLines={1}
      whiteSpace="nowrap"
      display="inline-flex"
      alignItems="center"
      flexWrap="nowrap"
      fontSize={fontSize ?? { base: 'lg', lg: 'md' }}
      color={dark ? DARK_TEXT_PRIMARY : 'gray.700'}
      border={border ?? (dark ? `1px solid ${DARK_GREY}` : '1px solid #E2E8F0')}
      borderRadius={dark ? '8px' : undefined}
      // Dark sort trigger mirrors the shared secondary-button chrome (Filter etc.)
      // so every dark toolbar control reads as one set: raised surface + grey
      // hairline, subtle white hover.
      background={background ?? (dark ? DARK_TABLE_HEADER_BG : 'white')}
      colorScheme={dark ? 'whiteAlpha' : 'white'}
      _hover={dark ? { background: 'rgba(255, 255, 255, 0.06)' } : undefined}
      _active={dark ? { background: 'rgba(255, 255, 255, 0.06)' } : undefined}
      fontWeight="normal"
      as={Button}
      // 20px of icon spacing is desktop chrome; on a narrow trigger it was enough
      // to push the label out from under its own box and into the chevron.
      iconSpacing={{ base: 2, md: 5 }}
      textAlign={{ base: isTable ? 'center' : 'left', md: 'left' }}
      rightIcon={<ChevronDownIcon boxSize={5} />}
      // The chevron keeps its size; the label is what gives way when the trigger
      // is narrower than its text (a raw text child cannot shrink, so it used to
      // overflow underneath the icon instead of truncating).
      sx={{ '& .chakra-button__icon': { flexShrink: 0 } }}
      _focus={{ boxShadow: isFocused ? 'outline' : 'none' }}
      height={height ?? { base: '48px', lg: '40px' }}
      isDisabled={isDisabled}
    >
      <Box as="span" display="block" flex="1 1 auto" minWidth={0} isTruncated textAlign="inherit">
        {item?.value !== undefined ? itemTitle : placeHolder}
      </Box>
    </MenuButton>
  )
}

export default DropdownButton
